import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useCrearDireccion } from '@/hooks/useOrdenesServicio'
import type { DireccionEnvio } from '@/types'

interface Props {
  dark: boolean
  /** Cliente al que se asocia la dirección. */
  cuenta: string
  nombreCliente?: string
  /** Empresa destino (la fija el rol externo; Empresa.All la elige en el pedido). */
  empresa: string
  onClose: () => void
  onCreated: (direccion: DireccionEnvio) => void
}

const apiError = (e: unknown): string => {
  const detail = (e as { response?: { data?: { detail?: unknown } } })?.response?.data?.detail
  if (typeof detail === 'string') return detail
  return (e as Error)?.message ?? ''
}

/** Alta de dirección de envío sobre un cliente existente (Dataverse).
 *  La app no da de alta clientes: eso lo hace administración en D365. */
export function DireccionNuevaForm({
  dark, cuenta, nombreCliente, empresa, onClose, onCreated,
}: Props) {
  const { t } = useTranslation()
  const crearDireccion = useCrearDireccion(cuenta, empresa)

  const [descripcion, setDescripcion] = useState('')
  const [calle, setCalle] = useState('')
  const [ciudad, setCiudad] = useState('')
  const [cp, setCp] = useState('')
  const [paisIso, setPaisIso] = useState('ES')
  const [error, setError] = useState<string | null>(null)

  const guardar = async () => {
    setError(null)
    if (!calle.trim() || !ciudad.trim()) {
      setError(t('ordenesServicio.direccionNueva.faltanCampos'))
      return
    }
    try {
      const creada = await crearDireccion.mutateAsync({
        empresa,
        descripcion: descripcion.trim(),
        calle: calle.trim(),
        ciudad: ciudad.trim(),
        cp: cp.trim(),
        paisIso: paisIso.trim() || 'ES',
      })
      onCreated(creada)
    } catch (e) {
      setError(apiError(e) || t('ordenesServicio.direccionNueva.errorGuardar'))
    }
  }

  const card = dark ? 'bg-slate-900 border-white/10' : 'bg-white border-slate-200'
  const label = `block text-[11px] font-semibold mb-1 ${dark ? 'text-slate-400' : 'text-slate-500'}`
  const input = `w-full min-h-[38px] px-3 rounded-lg border text-[13px] ${
    dark ? 'bg-white/5 border-white/15 text-slate-100' : 'bg-white border-slate-300 text-slate-800'
  }`
  const mainTxt = dark ? 'text-slate-100' : 'text-slate-900'
  const mutedTxt = dark ? 'text-slate-400' : 'text-slate-500'
  const btnPrimary = 'px-4 min-h-[40px] rounded-lg text-[13px] font-semibold bg-sky-600 text-white hover:bg-sky-500 disabled:opacity-50 transition-colors shadow-sm'
  const btnGhost = `px-4 min-h-[40px] rounded-lg text-[13px] font-semibold border transition-colors ${
    dark ? 'border-white/15 text-slate-300 hover:bg-white/10' : 'border-slate-300 text-slate-600 hover:bg-slate-100'
  }`

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-[1px]" onClick={onClose} />
      <div className={`relative w-full max-w-lg max-h-[90vh] overflow-y-auto rounded-2xl border shadow-2xl ${card}`}>
        <div className="flex items-center justify-between gap-3 px-5 py-4">
          <h2 className={`text-[15px] font-black tracking-tight ${mainTxt}`}>
            {t('ordenesServicio.direccionNueva.title')}
          </h2>
          <button type="button" onClick={onClose} className={`text-[13px] ${mutedTxt} hover:underline`}>✕</button>
        </div>

        <div className="px-5 pb-4 space-y-3">
          <p className={`text-[12px] ${mutedTxt}`}>
            {t('ordenesServicio.direccionNueva.hint', { cliente: nombreCliente || cuenta })}
          </p>

          <div>
            <label className={label}>{t('ordenesServicio.direccionNueva.descripcion')}</label>
            <input
              className={input}
              value={descripcion}
              onChange={(e) => setDescripcion(e.target.value)}
              placeholder={t('ordenesServicio.direccionNueva.descripcionPh')}
              autoFocus
            />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="sm:col-span-2">
              <label className={label}>{t('ordenesServicio.direccionNueva.calle')} *</label>
              <input className={input} value={calle} onChange={(e) => setCalle(e.target.value)} />
            </div>
            <div>
              <label className={label}>{t('ordenesServicio.direccionNueva.ciudad')} *</label>
              <input className={input} value={ciudad} onChange={(e) => setCiudad(e.target.value)} />
            </div>
            <div>
              <label className={label}>{t('ordenesServicio.direccionNueva.cp')}</label>
              <input className={input} value={cp} onChange={(e) => setCp(e.target.value)} />
            </div>
            <div>
              <label className={label}>{t('ordenesServicio.direccionNueva.pais')}</label>
              <input className={input} value={paisIso} maxLength={3} onChange={(e) => setPaisIso(e.target.value.toUpperCase())} />
            </div>
          </div>

          {error && <p className="text-[12px] text-red-500">{error}</p>}
        </div>

        <div className={`sticky bottom-0 flex items-center justify-end gap-2 px-5 py-4 border-t ${
          dark ? 'border-white/10 bg-slate-900' : 'border-slate-200 bg-white'
        }`}>
          <button type="button" onClick={onClose} className={btnGhost}>{t('ordenesServicio.form.cancelar')}</button>
          <button type="button" onClick={guardar} disabled={crearDireccion.isPending} className={btnPrimary}>
            {crearDireccion.isPending ? '…' : t('ordenesServicio.direccionNueva.crear')}
          </button>
        </div>
      </div>
    </div>
  )
}
